import { NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Dimensions, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { describeAccept } from '../file-validation';
import { fitToBox, INVALID_IMAGE_MESSAGE, loadImage, readAsDataUrl, underSizedMessage, validateImage } from '../image-processing';

/** Pick an image and crop it interactively to a fixed aspect; emits the cropped PNG data URL. */
@Component({
    selector: 'app-image-crop-dialog',
    templateUrl: './image-crop-dialog.component.html',
    standalone: true,
    imports: [NgIf, ImageCropperComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCropDialogComponent implements OnChanges {
    @Input() open = false;
    @Input() heading = 'Crop image';
    @Input() saveLabel = 'Save';
    @Input() aspectRatio = 1;
    @Input() outputWidth: number | null = null;
    @Input() outputHeight: number | null = null;
    @Input() accept = 'image/png,image/jpeg,image/gif,image/webp';
    @Input() maxBytes: number | null = null;
    @Input() initialFile: File | null = null;
    @Input() saving = false;

    @Output() save = new EventEmitter<string>();
    @Output() closed = new EventEmitter<void>();

    @ViewChild(ImageCropperComponent) cropper: ImageCropperComponent;

    file: File | null = null;
    loaded = false;
    underSized = false;
    cropping = false;
    error: string | null = null;
    transform: ImageTransform = {};
    private _scale = 1;

    constructor(private _cdr: ChangeDetectorRef) {}

    get width(): number {
        return this.outputWidth || Math.round((this.outputHeight || 250) * this.aspectRatio);
    }

    get height(): number {
        return this.outputHeight || Math.round(this.width / this.aspectRatio);
    }

    get hint(): string {
        const formats = describeAccept(this.accept);
        return `Recommended image size: ${this.width} x ${this.height}px.` + (formats ? ` Supported file types: ${formats}.` : '');
    }

    get underSizedText(): string {
        return underSizedMessage(this.width, this.height);
    }

    get busy(): boolean {
        return this.saving || this.cropping;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open'] && !this.open) {
            this.reset();
        }
        if (changes['initialFile'] && this.initialFile) {
            this.load(this.initialFile);
        }
    }

    onFiles(event: Event): void {
        this.load((event as CustomEvent<File[]>).detail?.[0]);
    }

    zoomIn(): void {
        this._scale += 0.1;
        this.transform = { ...this.transform, scale: this._scale };
    }

    zoomOut(): void {
        this._scale = Math.max(0.1, this._scale - 0.1);
        this.transform = { ...this.transform, scale: this._scale };
    }

    onImageLoaded(): void {
        this.loaded = true;
        this._cdr.markForCheck();
    }

    onCropperReady(event: Dimensions): void {
        this.underSized = event.width < this.width || event.height < this.height;
        this._cdr.markForCheck();
    }

    onLoadFailed(): void {
        this.reset();
        this.error = INVALID_IMAGE_MESSAGE;
        this._cdr.markForCheck();
    }

    reset(): void {
        this.file = null;
        this.loaded = false;
        this.underSized = false;
        this.cropping = false;
        this.error = null;
        this.transform = {};
        this._scale = 1;
        this._cdr.markForCheck();
    }

    async submit(): Promise<void> {
        if (!this.cropper || !this.loaded || this.busy) {
            return;
        }
        this.cropping = true;
        try {
            const cropped = this.cropper.crop('base64');
            if (!cropped?.base64) {
                throw new Error('The image could not be cropped. Choose another image.');
            }
            const result = await fitToBox(cropped.base64, this.width, this.height);
            this.save.emit(result.dataUrl);
        } catch (e) {
            this.error = (e as Error).message;
        }
        this.cropping = false;
        this._cdr.markForCheck();
    }

    close(): void {
        if (!this.saving) {
            this.closed.emit();
        }
    }

    private async load(file: File | undefined): Promise<void> {
        if (!file) {
            return;
        }
        const invalid = validateImage(file, this.accept, this.maxBytes);
        if (invalid) {
            this.reset();
            this.error = invalid;
            return;
        }
        try {
            await loadImage(await readAsDataUrl(file));
            this.reset();
            this.file = file;
        } catch (e) {
            this.reset();
            this.error = (e as Error).message;
        }
        this._cdr.markForCheck();
    }
}
